/**
 * LivePnlCard — real-time P&L for discretionary holdings.
 *
 * Shows:
 *   • Total discretionary value + today's change
 *   • Top gainer & top loser
 *   • Mini position list sorted by today's % change
 */
import { useEffect, useState, useCallback } from 'react'
import { TrendingUp, TrendingDown, RefreshCw, Activity } from 'lucide-react'
import { getLivePnl } from '@/lib/api'
import type { LivePnlResponse, LivePnlPosition } from '@/lib/types'

// ── Helpers ──────────────────────────────────────────────────────────────────

function fmtGbp(n: number, maximumFractionDigits = 0) {
  const prefix = n < 0 ? '-£' : '£'
  return `${prefix}${Math.abs(n).toLocaleString('en-GB', { maximumFractionDigits })}`
}

function fmtPct(n: number, signed = true) {
  const sign = signed && n > 0 ? '+' : ''
  return `${sign}${n.toFixed(2)}%`
}

function fmtPrice(n: number, currency: string) {
  const sym = currency === 'USD' ? '$' : '£'
  return `${sym}${n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

// ── Sub-components ────────────────────────────────────────────────────────────

function PerformerBadge({
  label,
  position,
  isGainer,
}: {
  label: string
  position: LivePnlPosition
  isGainer: boolean
}) {
  const color = isGainer ? 'text-emerald-400' : 'text-rose-400'
  const bg    = isGainer ? 'bg-emerald-900/30' : 'bg-rose-900/30'
  const sign  = isGainer ? '+' : ''
  return (
    <div className={`flex-1 rounded-lg px-3 py-2 ${bg}`}>
      <p className="text-[10px] text-text-muted uppercase tracking-wide mb-0.5">{label}</p>
      <p className="text-xs font-semibold text-text-primary truncate">{position.ticker}</p>
      <p className={`text-xs font-mono ${color}`}>
        {sign}{fmtPct(position.today_change_pct)}
      </p>
    </div>
  )
}

interface PositionRowProps {
  pos: LivePnlPosition
}

function PositionRow({ pos }: PositionRowProps) {
  const up = pos.today_change_pct >= 0
  return (
    <div className="flex items-center gap-2 py-1.5 border-b border-border/40 last:border-0">
      {/* Ticker + name */}
      <div className="w-20 shrink-0">
        <p className="text-xs font-mono font-semibold text-text-primary">{pos.ticker}</p>
        <p className="text-[10px] text-text-muted truncate">{pos.name}</p>
      </div>

      {/* Price + today change */}
      <div className="flex-1 min-w-0">
        <p className="text-xs text-text-secondary text-right font-mono">
          {fmtPrice(pos.current_price, pos.currency)}
        </p>
        <p className={`text-[10px] text-right font-mono ${up ? 'text-emerald-400' : 'text-rose-400'}`}>
          {up ? '+' : ''}{fmtPct(pos.today_change_pct)}
        </p>
      </div>

      {/* Unrealised P&L */}
      <div className="w-20 shrink-0 text-right">
        <p className={`text-xs font-mono ${pos.pnl >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
          {pos.pnl >= 0 ? '+' : ''}{fmtGbp(pos.pnl)}
        </p>
        <p className={`text-[10px] font-mono ${pos.pnl >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
          {fmtPct(pos.pnl_pct)}
        </p>
      </div>
    </div>
  )
}

// ── Main card ─────────────────────────────────────────────────────────────────

export default function LivePnlCard() {
  const [data, setData]       = useState<LivePnlResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError]     = useState<string | null>(null)
  const [refreshed, setRefreshed] = useState<Date | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await getLivePnl()
      setData(res)
      setRefreshed(new Date())
    } catch (e) {
      setError('Failed to load live P&L')
    } finally {
      setLoading(false)
    }
  }, [])

  // Initial load + auto-refresh every 60 s
  useEffect(() => {
    load()
    const id = setInterval(load, 60_000)
    return () => clearInterval(id)
  }, [load])

  const todayUp = (data?.today_change ?? 0) >= 0
  const totalUp = (data?.total_pnl ?? 0) >= 0

  return (
    <div className="card p-5">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Activity size={16} className="text-blue-400" aria-hidden />
          <h2 className="font-semibold text-text-primary">Live P&amp;L</h2>
          <span className="badge badge-blue text-[10px]">DISCRETIONARY</span>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="text-text-muted hover:text-text-primary transition-colors"
          aria-label="Refresh live P&L"
        >
          <RefreshCw size={13} className={loading ? 'animate-spin' : ''} aria-hidden />
        </button>
      </div>

      {error ? (
        <p className="text-sm text-rose-400">{error}</p>
      ) : loading && !data ? (
        <p className="text-sm text-text-muted animate-pulse">Loading prices…</p>
      ) : data ? (
        <>
          {/* KPI row */}
          <div className="grid grid-cols-2 gap-3 mb-4">
            {/* Total value + today */}
            <div>
              <p className="text-[10px] text-text-muted uppercase tracking-wide">Discretionary Value</p>
              <p className="text-lg font-bold font-mono text-text-primary">
                {fmtGbp(data.total_value)}
              </p>
              <div className={`flex items-center gap-1 text-xs font-mono mt-0.5 ${todayUp ? 'text-emerald-400' : 'text-rose-400'}`}>
                {todayUp
                  ? <TrendingUp size={11} aria-hidden />
                  : <TrendingDown size={11} aria-hidden />}
                <span>
                  {todayUp ? '+' : ''}{fmtGbp(data.today_change)} ({fmtPct(data.today_change_pct)}) today
                </span>
              </div>
            </div>

            {/* Total unrealised P&L */}
            <div>
              <p className="text-[10px] text-text-muted uppercase tracking-wide">Unrealised P&amp;L</p>
              <p className={`text-lg font-bold font-mono ${totalUp ? 'text-emerald-400' : 'text-rose-400'}`}>
                {totalUp ? '+' : ''}{fmtGbp(data.total_pnl)}
              </p>
              <p className={`text-xs font-mono mt-0.5 ${totalUp ? 'text-emerald-400' : 'text-rose-400'}`}>
                {fmtPct(data.total_pnl_pct)} total return
              </p>
            </div>
          </div>

          {/* Best / Worst today */}
          {(data.best_performer || data.worst_performer) && (
            <div className="flex gap-2 mb-4">
              {data.best_performer && (
                <PerformerBadge label="Top gainer" position={data.best_performer} isGainer={true} />
              )}
              {data.worst_performer && data.worst_performer.ticker !== data.best_performer?.ticker && (
                <PerformerBadge label="Top loser" position={data.worst_performer} isGainer={false} />
              )}
            </div>
          )}

          {/* Position list */}
          <div>
            <p className="text-[10px] text-text-muted uppercase tracking-wide mb-1">
              Positions — sorted by today&apos;s move
            </p>
            {data.positions.map(pos => (
              <PositionRow key={pos.ticker} pos={pos} />
            ))}
          </div>

          {/* Footer timestamp */}
          {refreshed && (
            <p className="text-[10px] text-text-muted mt-3 text-right">
              Updated {refreshed.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </p>
          )}
        </>
      ) : null}
    </div>
  )
}
